import { useEffect, useState } from 'react';
import { Table, SimpleGrid, Text, Stack, Image, Paper, Group, Button } from '@mantine/core';
import { IconAdd as IconPlus, IconCaliper } from '../../components/icons/CncIcons';
import EntityHeader from '../../components/EntityHeader';
import { ViewMode } from '../../components/TableGridToggle';
import { api, imageUrl } from '../../api/client';
import TechnicianModal from './TechnicianModal';

export interface Technician {
  id: number;
  uid: string;
  name: string;
  notes: string | null;
  hourlyRateEur: number;
  imagePath: string | null;
}

export default function TechniciansPage() {
  const [view, setView] = useState<ViewMode>('table');
  const [rows, setRows] = useState<Technician[]>([]);
  const [editing, setEditing] = useState<Technician | 'new' | null>(null);

  async function refresh() {
    const list = await api.get<Technician[]>('/technicians');
    setRows(list);
  }

  useEffect(() => { refresh(); }, []);

  return (
    <Stack>
      <EntityHeader title="Lab Technicians" icon={<IconCaliper size={26} />} view={view} onView={setView} />

      {view === 'table' ? (
        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>Image</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ width: 180 }}>Hourly Rate (EUR)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id} className="click-row" onClick={() => setEditing(r)}>
                  <Table.Td>
                    <div className="image-box">
                      {r.imagePath && <img src={imageUrl(r.imagePath, true)} alt={r.name} />}
                    </div>
                  </Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>{r.hourlyRateEur.toFixed(2)}</Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')} data-tour="technicians-add">
                    Add Technician
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {rows.map((r) => (
            <div key={r.id} className="grid-card" onClick={() => setEditing(r)}>
              <div className="card-image">
                {r.imagePath ? <img src={imageUrl(r.imagePath)} alt={r.name} /> : <Text c="dimmed" size="xs">No image</Text>}
              </div>
              <Group justify="space-between">
                <Text fw={600}>{r.name}</Text>
                <Text size="sm" c="orange">€{r.hourlyRateEur.toFixed(2)}/h</Text>
              </Group>
            </div>
          ))}
          <div className="add-card" onClick={() => setEditing('new')}>
            <Group gap={6}><IconPlus size={16} /><Text>Add Technician</Text></Group>
          </div>
        </SimpleGrid>
      )}

      {editing && (
        <TechnicianModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await refresh(); setEditing(null); }}
          onDeleted={async () => { await refresh(); setEditing(null); }}
        />
      )}
    </Stack>
  );
}
